import {
  IconDashboard,
  IconUsers,
  IconBuilding,
  IconFiles,
  IconPill,
  IconShield,
  IconCreditCard,
  IconForms,
  IconBook2,
  IconCertificate,
  IconClipboardList,
  IconActivity,
  IconInbox,
} from "@tabler/icons-react";
import { lazyNamed, type PortalDef } from "@livingsyncs/ui";
// Previously imported from "./app.portal" (frontend) — resolved (Task 5.2
// follow-up): profileRoute moved to @livingsyncs/ui after a real-caller
// audit confirmed it's genuinely shared. settingsRoute is NOT imported here
// (see the routes array below for why) — imported via the deep path, not
// the main barrel above — sharedRoutes.ts's own dependency chain
// (ProfilePage.tsx's whole tree) would otherwise ship in every consuming
// app's bundle unconditionally, not just the apps that mount admin/audit
// routes; see @livingsyncs/ui's index.ts for the full reasoning.
import { profileRoute } from "@livingsyncs/ui/src/router/sharedRoutes";

const AdminLayout = lazyNamed(
  () => import("@/components/AdminLayout"),
  "AdminLayout",
);

// ── Lazy admin pages ─────────────────────────────────────────────────────────
const AdminDashboardPage = lazyNamed(
  () => import("@/pages/admin/AdminDashboardPage"),
  "AdminDashboardPage",
);
const AdminUsersPage = lazyNamed(
  () => import("@/pages/admin/AdminUsersPage"),
  "AdminUsersPage",
);
const GroupHomesPage = lazyNamed(
  () => import("@/pages/admin/GroupHomesPage"),
  "GroupHomesPage",
);
const GroupHomeDetailPage = lazyNamed(
  () => import("@/pages/admin/GroupHomeDetailPage"),
  "GroupHomeDetailPage",
);
const FacilityPharmacyIntegrationPage = lazyNamed(
  () => import("@/pages/admin/FacilityPharmacyIntegrationPage"),
  "FacilityPharmacyIntegrationPage",
);
const AdminLegalPage = lazyNamed(
  () => import("@/pages/admin/LegalAdminPage"),
  "LegalAdminPage",
);
const LegalDocumentsRedirect = lazyNamed(
  () => import("@/pages/admin/LegalAdminPage"),
  "LegalDocumentsRedirect",
);
const PharmacyPartnersPage = lazyNamed(
  () => import("@/pages/admin/PharmacyPartnersPage"),
  "PharmacyPartnersPage",
);
const AuditLogsPage = lazyNamed(
  () => import("@/pages/admin/AuditLogsPage"),
  "AuditLogsPage",
);
const SubscriptionsPage = lazyNamed(
  () => import("@/pages/admin/SubscriptionsPage"),
  "SubscriptionsPage",
);
const AuditorGroupsPage = lazyNamed(
  () => import("@/pages/admin/AuditorGroupsPage"),
  "AuditorGroupsPage",
);
const AuditorGroupDetailPage = lazyNamed(
  () => import("@/pages/admin/AuditorGroupDetailPage"),
  "AuditorGroupDetailPage",
);
const FormTemplatesPage = lazyNamed(
  () => import("@/pages/admin/forms/FormTemplatesPage"),
  "FormTemplatesPage",
);
const FormTemplateDetailPage = lazyNamed(
  () => import("@/pages/admin/forms/FormTemplateDetailPage"),
  "FormTemplateDetailPage",
);
const FormBuilderPage = lazyNamed(
  () => import("@/pages/admin/forms/FormBuilderPage"),
  "FormBuilderPage",
);
const FormVersionHistoryPage = lazyNamed(
  () => import("@/pages/admin/forms/FormVersionHistoryPage"),
  "FormVersionHistoryPage",
);
const RequestsPage = lazyNamed(
  () => import("@/pages/admin/requests/RequestsPage"),
  "RequestsPage",
);
const RuleLibraryPage = lazyNamed(
  () => import("@/pages/admin/rule-library/RuleLibraryPage"),
  "RuleLibraryPage",
);
const RuleLibraryDetailPage = lazyNamed(
  () => import("@/pages/admin/rule-library/RuleLibraryDetailPage"),
  "RuleLibraryDetailPage",
);
const TrainingMaterialsList = lazyNamed(
  () => import("@/features/training/TrainingMaterialsList"),
  "TrainingMaterialsList",
);
const TrainingMaterialNewPage = lazyNamed(
  () => import("@/pages/admin/TrainingMaterialNewPage"),
  "TrainingMaterialNewPage",
);
const TrainingMaterialEditPage = lazyNamed(
  () => import("@/pages/admin/TrainingMaterialEditPage"),
  "TrainingMaterialEditPage",
);
const SystemHealthPage = lazyNamed(
  () => import("@/pages/admin/SystemHealthPage"),
  "SystemHealthPage",
);

/**
 * The Admin portal authored as data (US-044 AC6/AC12). Now a self-contained
 * portal at `/admin` with its own `AdminLayout` — previously these routes lived
 * under `/` `AppLayout` behind `AdminRoute`. Portal membership is enforced by
 * `PortalBoundary portal="admin"`, so no per-route `access` is needed (it maps
 * 1:1 to the old `AdminRoute` platform:manage gate). Paths are relative to the
 * `/admin` base.
 *
 * The settings subtree is shared with the app portal so platform admins (who
 * are bounced from the app portal) keep settings access at `/admin/settings`.
 */
export const adminPortal: PortalDef = {
  id: "admin",
  basePath: "/admin",
  layout: AdminLayout,
  routes: [
    {
      index: true,
      Component: AdminDashboardPage,
      nav: { label: "Admin Dashboard", icon: IconDashboard, order: 1 },
    },
    {
      path: "users",
      Component: AdminUsersPage,
      nav: { label: "Users", icon: IconUsers, order: 2 },
    },
    {
      path: "group-homes",
      Component: GroupHomesPage,
      nav: { label: "Group Homes", icon: IconBuilding, order: 3 },
    },
    { path: "group-homes/:id", Component: GroupHomeDetailPage },
    {
      path: "facilities/:facilityId/pharmacy-integration",
      Component: FacilityPharmacyIntegrationPage,
    },
    {
      path: "pharmacy-partners",
      Component: PharmacyPartnersPage,
      nav: { label: "Pharmacy Partners", icon: IconPill, order: 4 },
    },
    // US-143 — execution state and the document catalog on one page, as two
    // tabs. Platform membership is enforced by PortalBoundary, so admin routes
    // carry no per-route access (see the admin route-parity test).
    {
      path: "legal-packets",
      Component: AdminLegalPage,
      nav: { label: "Legal", icon: IconFiles, order: 11 },
    },
    // `/admin/legal` was the Documents page and is still in bookmarks and in
    // links we have already sent. It keeps working, landing on the tab it used
    // to be. No `nav` — one sidebar entry for one page.
    {
      path: "legal",
      Component: LegalDocumentsRedirect,
      nav: undefined,
    },
    {
      path: "audit-logs",
      Component: AuditLogsPage,
      nav: { label: "Audit Logs", icon: IconClipboardList, order: 10 },
    },
    {
      path: "subscriptions",
      Component: SubscriptionsPage,
      nav: { label: "Subscriptions", icon: IconCreditCard, order: 6 },
    },
    {
      path: "auditor-groups",
      Component: AuditorGroupsPage,
      nav: { label: "Auditor Management", icon: IconShield, order: 5 },
    },
    { path: "auditor-groups/:id", Component: AuditorGroupDetailPage },
    {
      path: "forms",
      Component: FormTemplatesPage,
      nav: { label: "Form Templates", icon: IconForms, order: 7 },
    },
    { path: "forms/:id", Component: FormTemplateDetailPage },
    { path: "forms/new", Component: FormBuilderPage },
    { path: "forms/:id/edit", Component: FormBuilderPage },
    { path: "forms/:id/versions", Component: FormVersionHistoryPage },
    {
      path: "requests",
      Component: RequestsPage,
      nav: {
        label: "Requests",
        icon: IconInbox,
        order: 7,
        anyOf: ["request:review"],
      },
    },
    {
      path: "rule-library",
      Component: RuleLibraryPage,
      nav: {
        label: "Rule Library",
        icon: IconBook2,
        order: 8,
        anyOf: ["rule-bundle:write"],
      },
    },
    { path: "rule-library/:id", Component: RuleLibraryDetailPage },
    {
      path: "training",
      Component: TrainingMaterialsList,
      nav: { label: "Training Materials", icon: IconCertificate, order: 9 },
    },
    { path: "training/new", Component: TrainingMaterialNewPage },
    { path: "training/:id/edit", Component: TrainingMaterialEditPage },
    {
      path: "system",
      Component: SystemHealthPage,
      nav: { label: "System Health", icon: IconActivity, order: 11 },
    },

    // Personal account page, reached from the header account menu.
    profileRoute,

    // settingsRoute (organization/facility/audit-logs/legal tabs) was
    // mounted here too, but every tab in it is scoped to a role a platform
    // admin/manager never holds: organization/facility need
    // isGroupHomeAdmin/role:facility:manager, and audit-logs/legal/
    // incoming-transfers are app-portal-only (SettingsPage.tsx now enforces
    // that, closing a gap where they leaked in here too) and duplicate
    // Admin's own dedicated Audit Logs/Legal sidebar pages above. The
    // result was a Settings nav item that always landed on an empty tab
    // list for every user who could reach this portal. Not mounted.
  ],
};
